/**
 * Maps `items` through `worker` with at most `limit` calls in flight,
 * returning results in input order.
 *
 * Fail closed: the first rejection stops claiming remaining items and aborts
 * the `signal` handed to in-flight workers, then rethrows that error. An
 * external `opts.signal` abort does the same and rejects with its reason.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number, signal: AbortSignal) => Promise<R>,
  opts?: { signal?: AbortSignal }
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  let firstError: unknown;
  // Fail closed: once one item refuses, stop claiming remaining work and
  // abort in-flight calls so an all-or-nothing caller does not keep doing
  // (or minting) work whose result will be thrown away.
  const abort = new AbortController();
  const onExternalAbort = () => {
    firstError ??= opts?.signal?.reason ?? new Error('Aborted');
    nextIndex = items.length;
    abort.abort();
  };
  if (opts?.signal?.aborted) onExternalAbort();
  opts?.signal?.addEventListener('abort', onExternalAbort, { once: true });

  try {
    await Promise.all(
      Array.from(
        { length: Math.max(1, Math.min(limit, items.length)) },
        async () => {
          while (!abort.signal.aborted) {
            const index = nextIndex++;
            if (index >= items.length) return;
            try {
              results[index] = await worker(items[index], index, abort.signal);
            } catch (error) {
              firstError ??= error;
              nextIndex = items.length;
              abort.abort();
              return;
            }
          }
        }
      )
    );
  } finally {
    opts?.signal?.removeEventListener('abort', onExternalAbort);
  }

  if (firstError !== undefined) throw firstError;
  return results;
}
